import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { useEffect, useState, useCallback } from "react";
import { Search, Trash2, Check, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import featureGatingService from "../services/featureGatingService";

const initialsOf = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Deterministic soft avatar color per uid.
const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
  "bg-rose-100 text-rose-700",
  "bg-sky-100 text-sky-700",
];
const colorFor = (seed) => {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

function MemberSearch({ team, onTeamUpdate, onDone }) {
  const { globalUser } = useAuth();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checked, setChecked] = useState({}); // uid -> true
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState({}); // uid -> true
  const [memberDetails, setMemberDetails] = useState({});
  const [ownerDetail, setOwnerDetail] = useState(null);
  const [limitInfo, setLimitInfo] = useState(null);

  const teamMembers = Array.isArray(team?.members) ? team.members : [];

  const fetchLimit = useCallback(async () => {
    if (!globalUser?.uid) return;
    try {
      setLimitInfo(await featureGatingService.canAddTeamMember(globalUser.uid));
    } catch (e) {
      console.error("limit info:", e);
    }
  }, [globalUser?.uid]);

  useEffect(() => {
    fetchLimit();
  }, [fetchLimit]);

  // Resolve owner + member uids → name/email for the right-hand list.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ids = new Set(teamMembers);
      if (team?.ownerId) ids.add(team.ownerId);
      const entries = await Promise.all(
        [...ids].map(async (uid) => {
          try {
            let snap = await getDoc(doc(db, "lawyers", uid));
            let isClient = false;
            if (!snap.exists()) {
              snap = await getDoc(doc(db, "client", uid));
              isClient = true;
            }
            const d = snap.exists() ? snap.data() : {};
            return [uid, { name: d.name || d.displayName || "Unknown", email: d.email || "", isClient }];
          } catch {
            return [uid, { name: "Unknown", email: "" }];
          }
        })
      );
      if (cancelled) return;
      const map = Object.fromEntries(entries);
      setMemberDetails(map);
      if (team?.ownerId) setOwnerDetail(map[team.ownerId] || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [team?.members, team?.ownerId]); // eslint-disable-line

  // Debounced available-member search (lawyers by name / email prefix).
  useEffect(() => {
    const handle = setTimeout(async () => {
      const q = term.trim();
      if (!q) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const end = q + String.fromCharCode(0xf8ff);
        const byId = new Map();
        for (const attr of ["name", "email"]) {
          const snap = await getDocs(
            query(
              collection(db, "lawyers"),
              where(attr, ">=", q),
              where(attr, "<=", end),
              limit(10)
            )
          );
          snap.forEach((d) => {
            // Exclude self (owner) and users already on the team.
            if (d.id !== globalUser?.uid && !teamMembers.includes(d.id))
              byId.set(d.id, { uid: d.id, ...d.data() });
          });
        }
        setResults([...byId.values()]);
      } catch (e) {
        console.error("member search:", e);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [term, globalUser, team?.members]); // eslint-disable-line

  const toggleCheck = (uid) =>
    setChecked((prev) => {
      const next = { ...prev };
      if (next[uid]) delete next[uid];
      else next[uid] = true;
      return next;
    });

  const handleAddSelected = async () => {
    const uids = Object.keys(checked);
    if (uids.length === 0) return;
    if (limitInfo && !limitInfo.canAdd) {
      toast.error(limitInfo.upgradeMessage || "Team member limit reached.");
      return;
    }
    setAdding(true);
    try {
      await updateDoc(doc(db, "teams", team.id), { members: arrayUnion(...uids) });
      setChecked({});
      setTerm("");
      setResults([]);
      toast.success(`${uids.length} member${uids.length === 1 ? "" : "s"} added`);
      if (onTeamUpdate) onTeamUpdate();
      fetchLimit();
    } catch (e) {
      console.error("Error adding members:", e);
      toast.error("Failed to add members.");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (uid) => {
    setRemoving((prev) => ({ ...prev, [uid]: true }));
    try {
      await updateDoc(doc(db, "teams", team.id), { members: arrayRemove(uid) });
      toast.success("Member removed");
      if (onTeamUpdate) onTeamUpdate();
      fetchLimit();
    } catch (e) {
      console.error("Error removing member:", e);
      toast.error("Failed to remove member.");
    } finally {
      setRemoving((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  };

  const checkedCount = Object.keys(checked).length;
  const cardCls = "flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm";
  const fieldCls =
    "w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/40";

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
      {/* ===== Left: available members ===== */}
      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-800">Available Members</h3>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search member..."
            className={fieldCls}
          />
        </div>

        <div className="mt-3 min-h-[240px] flex-grow space-y-1.5 overflow-y-auto pr-1">
          {searching && (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          )}
          {!searching && results.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-400">
              {term.trim() ? "No matching members found." : "Search to find members to add."}
            </p>
          )}
          {results.map((u) => (
            <label
              key={u.uid}
              className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-100 px-3 py-2 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={!!checked[u.uid]}
                onChange={() => toggleCheck(u.uid)}
                className="h-4 w-4 shrink-0 accent-yellow-500"
              />
              <div className="min-w-0 flex-grow">
                <p className="truncate text-sm font-medium text-slate-800">{u.name || "Unnamed"}</p>
                <p className="truncate text-xs text-slate-400">{u.email || "No email"}</p>
              </div>
            </label>
          ))}
        </div>

        <button
          type="button"
          disabled={checkedCount === 0 || adding}
          onClick={handleAddSelected}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-yellow-500 px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600 disabled:opacity-50"
        >
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Add Selected{checkedCount > 0 ? ` (${checkedCount})` : ""}
        </button>
      </div>

      {/* ===== Right: current team members ===== */}
      <div className={cardCls}>
        <h3 className="text-sm font-semibold text-slate-800">
          Team Members ({teamMembers.length})
        </h3>

        <div className="mt-3 space-y-2 overflow-y-auto">
          {/* Owner = Team Lead (not removable) */}
          {team?.ownerId && (
            <div className="flex items-center gap-3 rounded-xl px-2 py-2">
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${colorFor(
                  team.ownerId
                )}`}
              >
                {initialsOf(ownerDetail?.name)}
              </span>
              <div className="min-w-0 flex-grow">
                <p className="truncate text-sm font-medium text-slate-800">
                  {ownerDetail?.name || "You"}
                </p>
                {ownerDetail?.email && (
                  <p className="truncate text-xs text-slate-400">{ownerDetail.email}</p>
                )}
              </div>
              <span className="shrink-0 rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-inset ring-violet-200">
                Team Lead
              </span>
              <span className="w-6" />
            </div>
          )}

          {teamMembers.length === 0 && (
            <p className="py-6 text-center text-sm text-slate-400">
              No members yet. Add members from the left.
            </p>
          )}

          {teamMembers.map((uid) => {
            const d = memberDetails[uid] || { name: "Unknown", email: "" };
            const busy = !!removing[uid];
            return (
              <div key={uid} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-slate-50">
                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${colorFor(
                    uid
                  )}`}
                >
                  {initialsOf(d.name)}
                </span>
                <div className="min-w-0 flex-grow">
                  <p className="truncate text-sm font-medium text-slate-800">{d.name}</p>
                  {d.email && <p className="truncate text-xs text-slate-400">{d.email}</p>}
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-inset ring-slate-200">
                  Member
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleRemove(uid)}
                  title="Remove member"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-red-500 transition-colors hover:bg-red-50 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => onDone && onDone()}
          className="mt-5 inline-flex items-center justify-center gap-2 self-start rounded-lg bg-yellow-500 px-6 py-2.5 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-yellow-600"
        >
          <Check className="h-4 w-4" /> Done
        </button>
      </div>
    </div>
  );
}

export default MemberSearch;
